import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { outreach, outreachRevision, download, utc, localDate, label, reportCSV } from "../components/outreachApi";
import Api, { BASE_URL } from "../../../../shared/api/api";
import OutreachProspectPicker from "../components/OutreachProspectPicker";
import OutreachScoreForm from "../components/OutreachScoreForm";
import useOutreachDiscoverySession,{discoveryLifetime} from "../components/useOutreachDiscoverySession";
import OutreachRichText from "../components/OutreachRichText";
import OutreachVideo from "../components/OutreachVideo";
import OutreachModal from "../components/OutreachModal";
import "../styles/AdminBookstores.css";
import "../styles/AdminOutreach.css";
const sections = [["dashboard", "Dashboard"], ["discovery", "Prospect Discovery"], ["prospects", "Prospect Directory"], ["campaigns", "Campaigns"], ["communications", "Communications"], ["followups", "Follow-ups"], ["applications", "Collaboration Applications"], ["events", "Events & Interviews"], ["analytics", "Analytics"], ["templates", "Templates"], ["settings", "Settings & Integrations"]];
const empty = {
  categories: [],
  books: [],
  campaigns: [],
  stages: [],
  methods: []
};
const date = value => value ? new Date(value).toLocaleString() : "—";
const safeLink = value => {
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
};
function PublicLink({
  href,
  children,
  className = "outreach-link-button"
}) {
  return safeLink(href) ? <a className={className} href={safeLink(href)} target="_blank" rel="noopener noreferrer">{children || href}</a> : null;
}
function Field({
  title,
  name,
  value,
  onChange,
  type = "text",
  options,
  required,
  ...rest
}) {
  if (type === "prospect") return <div className="outreach-field outreach-prospect-field"><span>{title}</span><OutreachProspectPicker name={name} value={value} onChange={onChange} required={required} /></div>;
  return <label className="outreach-field">{title}{options ? <select name={name} value={value ?? ""} onChange={onChange} required={required} {...rest}><option value="">Choose…</option>{options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? label(o)}</option>)}</select> : type === "textarea" ? <textarea name={name} value={value ?? ""} onChange={onChange} required={required} rows={5} {...rest} /> : <input type={type} name={name} value={value ?? ""} onChange={onChange} required={required} {...rest} />}</label>;
}
function useFields(initial) {
  const [value, set] = useState(initial);
  return [value, e => set(v => ({
    ...v,
    [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value
  })), set];
}
function Table({
  headers,
  rows,
  emptyText = "No records yet."
}) {
  return <div className="bookstore-table-wrap"><table className="bookstore-table"><thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead><tbody>{rows.length ? rows : <tr><td colSpan={headers.length}>{emptyText}</td></tr>}</tbody></table></div>;
}
function ProspectLink({
  id,
  children
}) {
  return <Link className="outreach-link-button" to={`/admin/outreach/prospects/${id}`}>{children}</Link>;
}
function prospectTitle(data) {
  const p = data.prospect;
  return p.display_name || (p.platform === "youtube" && p.name.startsWith("YouTube channel ")
    ? data.sources.find(source => source.provider === "youtube" && source.metadata?.name)?.metadata.name : null) || p.name;
}
function BookSelect({
  books,
  value = [],
  onChange
}) {
  return <fieldset className="outreach-books"><legend>Associated books</legend>{books.map(book => <label key={book.title}><input type="checkbox" checked={value.includes(book.title)} onChange={e => onChange(e.target.checked ? [...value, book.title] : value.filter(t => t !== book.title))} />{book.title}</label>)}</fieldset>;
}
export default function AdminOutreach() {
  const [discoverySession,setDiscoverySession]=useOutreachDiscoverySession();
  const location = useLocation(),
    navigate = useNavigate();
  const parts = location.pathname.slice("/admin/outreach".length).split("/").filter(Boolean),
    section = parts[0] || "dashboard",
    prospectId = section === "prospects" && parts[1] ? parts[1] : null;
  const [base, setBase] = useState(empty),
    [data, setData] = useState(null),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [revision, setRevision] = useState(0);
  const seq = useRef(0),
    loadedKey = useRef("");
  const refresh = useCallback(() => setRevision(v => v + 1), []);
  useEffect(() => {
    let live = true;
    outreach("bootstrap").then(r => {
      if (live) setBase(r);
    }).catch(e => {
      if (live) setError(e.message);
    });
    return () => {
      live = false;
    };
  }, [revision]);
  useEffect(() => {
    const current = ++seq.current;
    const routeKey = `${section}/${prospectId || ""}`;
    if (loadedKey.current !== routeKey) {
      setNotice("");
      setError("");
      setLoading(true);
      setData(null);
      loadedKey.current = routeKey;
    }
    const path = prospectId ? `prospects/${prospectId}` : {
      dashboard: "dashboard",
      prospects: "prospects",
      campaigns: "campaigns",
      communications: "messages",
      followups: "tasks",
      applications: "applications",
      events: "events",
      analytics: "analytics",
      templates: "templates",
      settings: "settings",
      discovery: "saved-searches"
    }[section];
    if (!path) {
      setError("Section not found.");
      setLoading(false);
      return;
    }
    outreach(path).then(r => {
      if (current === seq.current) setData(r);
    }).catch(e => {
      if (current === seq.current) setError(e.message);
    }).finally(() => {
      if (current === seq.current) setLoading(false);
    });
    return () => {
      seq.current++;
    };
  }, [section, prospectId, revision]);
  const act = async (fn, message = "Saved.") => {
    const before = outreachRevision();
    setBusy(true);
    setError("");
    try {
      const result = await fn();
      setNotice(message);
      if (outreachRevision() !== before) refresh();
      return result;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setBusy(false);
    }
  };
  const exportRows = type => act(async () => {
    const r = await outreach(`export/${type}`);
    download(r.csv, r.filename, "text/csv;charset=utf-8");
  }, "Export downloaded (up to 10,000 rows).");
  const props = {
    base,
    data,
    act,
    busy,
    error,
    refresh,
    navigate,
    exportRows,
    discoverySession,
    setDiscoverySession
  };
  return <main className="bookstore-page outreach-page"><header className="bookstore-header"><div><h1>Outreach Manager</h1><p>Discover people, build relationships, and track opportunities.</p></div><Link to="/collaborate">Public collaboration page</Link></header>
 <nav className="outreach-nav" aria-label="Outreach sections">{sections.map(([key, title]) => <Link key={key} to={`/admin/outreach/${key}`} aria-current={section === key ? "page" : undefined}>{title}</Link>)}</nav>
 {notice && <p className="bookstore-notice" role="status">{notice}</p>}{error && <p className="bookstore-error" role="alert">{error}<button onClick={refresh}>Retry</button></p>}
 {loading || loadedKey.current !== `${section}/${prospectId || ""}` ? <p role="status">Loading outreach…</p> : data && <fieldset className="outreach-workspace" disabled={busy}>
 {section === "dashboard" && <Dashboard {...props} />}{section === "prospects" && (prospectId ? <ProspectDetail {...props} key={prospectId} /> : <Directory {...props} />)}
 {section === "discovery" && <Discovery {...props} />}{section === "campaigns" && <Campaigns {...props} />}{section === "communications" && <Communications {...props} />}
 {section === "followups" && <Tasks {...props} />}{section === "applications" && <Applications {...props} />}{section === "events" && <Events {...props} />}
 {section === "analytics" && <Analytics {...props} />}{section === "templates" && <Templates {...props} />}{section === "settings" && <Settings {...props} />}
 </fieldset>}</main>;
}
function Dashboard({
  data
}) {
  return <><div className="bookstore-summary">{[["Prospects", data.total_prospects], ["New in 30 days", data.new_prospects], ["Contacted", data.contacted], ["Responses", data.responses], ["Accepted collaborations", data.accepted], ["New applications", data.new_applications]].map(([title, n]) => <div key={title}><span>{title}<strong>{n || 0}</strong></span></div>)}</div>
 <div className="outreach-columns"><section><h2>Recent prospects</h2><ul>{data.recent.map(p => <li key={p.id}><ProspectLink id={p.id}>{p.display_name || p.name}</ProspectLink></li>)}</ul>{!data.recent.length && <p>Your directory is ready for its first prospect.</p>}<h2>Administrator priorities</h2><ul>{data.priority.map(p => <li key={p.id}><ProspectLink id={p.id}>{p.name}</ProspectLink> — {p.manual_priority}/100</li>)}</ul></section>
 <section><h2>Due and upcoming follow-ups</h2><ul>{data.tasks.map(t => <li key={t.id}><ProspectLink id={t.prospect_id}>{t.name}</ProspectLink>: {t.title} — {date(t.due_at)}</li>)}</ul><h2>Upcoming events</h2><ul>{data.events.map(e => <li key={e.id}>{e.title} — {date(e.starts_at)}</li>)}</ul></section></div>
 {!!data.failures.length && <p className="bookstore-error">{data.failures.length} recent email attempts need review in Communications.</p>}{data.health.map(h => <p role="status" key={h.provider}>{h.provider}: {h.last_error}</p>)}
 <h2>Recent activity</h2><Table headers={["When", "Action", "Record", "Administrator"]} rows={data.activity.map(a => <tr key={a.id}><td>{date(a.created_at)}</td><td>{label(a.action)}</td><td>{a.entity_type} {a.entity_id}</td><td>{a.actor}</td></tr>)} /></>;
}
function ProspectForm({
  base,
  formId,
  initial = {},
  onSave,
  busy = false
}) {
  const [v, change, set] = useFields({
    ...initial,
    name: initial.display_name || initial.name || "",
    email: initial.contacts?.find(c => c.method === "email")?.value || "",
    books: initial.books || [],
    category_ids: initial.category_ids || [],
    contacts: initial.contacts || []
  });
  const initialName = useRef(initial.display_name || initial.name || "");
  useEffect(() => {
    const nextName = initial.display_name || initial.name || "";
    const previousName = initialName.current;
    initialName.current = nextName;
    set(previous => previous.name === previousName ? {...previous, name: nextName} : previous);
  }, [initial.display_name, initial.name, set]);
  return <form id={formId} className="outreach-profile-form" onSubmit={e => {
    e.preventDefault();
    const emailIndex = v.contacts.findIndex(c => c.method === "email");
    const existingEmail = v.contacts[emailIndex];
    const contacts = v.contacts.filter((c, index) => index !== emailIndex);
    if (v.email.trim()) contacts.unshift(existingEmail?.value === v.email.trim() ? existingEmail : { method: "email", value: v.email.trim() });
    onSave({
      ...v,
      name: initial.display_name && v.name === initial.display_name ? initial.name : v.name,
      contacts,
      category_ids: v.category_ids.map(Number)
    });
  }}><div className="bookstore-fields">{[["name", "Name", true], ["organization", "Organization"], ["organization_type", "Organization type"], ["platform", "Platform"], ["website", "Website"], ["country", "Country"], ["region", "Region"], ["language", "Language"], ["genres", "Genres"], ["tags", "Tags"], ["preferred_contact", "Preferred contact method"]].map(([name, title, required]) => <Field key={name} name={name} title={title} value={v[name]} onChange={change} required={required} />)}
 <Field title="Email" type="email" name="email" value={v.email} onChange={change} /><Field title="Review status" name="review_state" value={v.review_state || "researching"} onChange={change} options={["researching", "ready"]} /><Field title="Administrator priority (0–100)" type="number" min="0" max="100" name="manual_priority" value={v.manual_priority} onChange={change} />
 </div><fieldset className="outreach-books"><legend>Categories</legend>{base.categories.map(c => <label key={c.id}><input type="checkbox" checked={v.category_ids.includes(c.id)} onChange={e => set({
          ...v,
          category_ids: e.target.checked ? [...v.category_ids, c.id] : v.category_ids.filter(id => id !== c.id)
        })} />{c.name}</label>)}</fieldset>
 <BookSelect books={base.books} value={v.books} onChange={books => set({
      ...v,
      books
    })} />
 <div className="outreach-profile-editors">{[["description", "Description"], ["notes", "Notes"], ["contact_instructions", "Submission guidelines"]].map(([name, title]) => <OutreachRichText key={name} title={title} value={v[name]} readOnly={busy} onChange={value => set(previous => ({...previous, [name]: value}))} />)}</div>
 <details><summary>Book club, guest, and organization details</summary><div className="bookstore-fields">{[["group_size", "Public or supplied group size", "number"], ["meeting_frequency", "Meeting frequency", "text"], ["meeting_format", "Meeting format", ["online", "in_person", "hybrid"]], ["reading_starts_at", "Reading starts", "date"], ["reading_ends_at", "Reading ends", "date"], ["guest_biography", "Guest biography", "textarea"], ["published_work", "Published books / public work links", "textarea"], ["interview_topics", "Proposed topics and interview questions", "textarea"], ["submission_guidelines", "Review / guest submission policy", "textarea"], ["event_availability", "Library / bookstore event availability", "textarea"]].map(([name, title, type]) => <Field key={name} title={title} value={v.profile?.[name]} type={Array.isArray(type) ? "text" : type} options={Array.isArray(type) ? type : undefined} onChange={e => set({
          ...v,
          profile: {
            ...v.profile,
            [name]: e.target.value
          }
        })} />)}</div></details>
 {!formId && <div className="bookstore-actions"><button type="submit">Save prospect</button></div>}</form>;
}
function Directory({
  base,
  data,
  act,
  busy,
  error,
  navigate,
  exportRows
}) {
  const [list, setList] = useState(data),
    [show, setShow] = useState(false),
    [query, change, setQuery] = useFields({
      search: "",
      page: 1
    }),
    [selected, setSelected] = useState([]),
    [bulk, bulkChange] = useFields({
      action: "tag",
      value: ""
    }),
    [csv, setCsv] = useState(""),
    [summary, setSummary] = useState(null);
  useEffect(() => setList(data), [data]);
  const load = page => act(async () => {
    const r = await outreach("prospects", {
      ...query,
      page
    });
    setList(r);
    setQuery(v => ({
      ...v,
      page
    }));
  }, "Directory updated.");
  return <><div className="bookstore-header"><h2>Prospect Directory</h2><div className="bookstore-actions"><button onClick={() => setShow(true)}>Add prospect</button><button onClick={() => exportRows("prospects")}>Export CSV</button></div></div>
 {show && <OutreachModal title="Create prospect" submitLabel="Save prospect" busy={busy} error={error} onClose={() => setShow(false)}>{formId => <ProspectForm formId={formId} base={base} busy={busy} onSave={v => act(async () => {
      const r = await outreach("prospects", v, "post");
      navigate(`/admin/outreach/prospects/${r.id}`);
    })} />}</OutreachModal>}
 <form className="outreach-filter outreach-directory-filter" onSubmit={e => {
      e.preventDefault();
      load(1);
    }}><Field title="Search name, organization, genre or tags" name="search" value={query.search} onChange={change} /><Field title="Category" name="category_id" value={query.category_id} onChange={change} options={base.categories.map(c => ({
        value: c.id,
        label: c.name
      }))} /><Field title="Campaign" name="campaign_id" value={query.campaign_id} onChange={change} options={base.campaigns.map(c => ({
        value: c.id,
        label: c.title
      }))} /><Field title="Platform" name="platform" value={query.platform} onChange={change} /><Field title="Country" name="country" value={query.country} onChange={change} /><Field title="Campaign stage" name="stage" value={query.stage} onChange={change} options={base.stages} /><Field title="Sort" name="sort" value={query.sort || "date"} onChange={change} options={["date", "name", "priority", "activity"]} /><label><input type="checkbox" name="archived" checked={Boolean(query.archived)} onChange={change} />Include archived prospects</label><button>Apply filters</button></form>
 <Table headers={["Select", "Prospect", "Platform", "Country", "Review", "Priority"]} rows={list.prospects.map(p => <tr key={p.id}><td><input type="checkbox" aria-label={`Select ${p.display_name || p.name}`} checked={selected.includes(p.id)} onChange={e => setSelected(e.target.checked ? [...selected, p.id] : selected.filter(id => id !== p.id))} /></td><td><ProspectLink id={p.id}>{p.display_name || p.name}</ProspectLink>{p.do_not_contact === 1 && <strong> — Do Not Contact</strong>}</td><td>{p.platform || "—"}</td><td>{p.country || "—"}</td><td>{label(p.review_state)}</td><td>{p.manual_priority ?? "Unassigned"}</td></tr>)} />
 <div className="bookstore-pagination"><span>{list.total} records · Page {list.page}</span><button disabled={list.page <= 1} onClick={() => load(list.page - 1)}>Previous</button><button disabled={list.page * 50 >= list.total} onClick={() => load(list.page + 1)}>Next</button></div>
 <form className="outreach-filter outreach-directory-filter" onSubmit={e => {
      e.preventDefault();
      act(() => outreach("bulk", {
        ...bulk,
        ids: selected
      }, "post"));
    }}><Field title={`Action on ${selected.length} selected prospects`} name="action" value={bulk.action} onChange={bulkChange} options={["tag", "campaign", "category", "review", "archive"]} /><Field title={bulk.action === "campaign" ? "Campaign" : bulk.action === "category" ? "Category" : "Tag text"} name="value" value={bulk.value} onChange={bulkChange} options={bulk.action === "campaign" ? base.campaigns.map(c => ({
        value: c.id,
        label: c.title
      })) : bulk.action === "category" ? base.categories.map(c => ({
        value: c.id,
        label: c.name
      })) : undefined} /><button disabled={!selected.length}>Apply selected action</button></form>
 <details><summary>Import CSV</summary><p>Headers: name (required), organization, email, website, platform, country, region, genres, tags, notes, source_url. Up to 1,000 rows / 2 MB. Duplicates are skipped for review.</p><input aria-label="Choose prospect CSV" type="file" accept=".csv,text/csv" onChange={async e => {
        const f = e.target.files[0];
        if (f) setCsv(await f.text());
        setSummary(null);
      }} /><button disabled={!csv} onClick={() => act(async () => {
        setSummary(await outreach("import/preview", {
          csv
        }, "post"));
      }, "Import preview ready.")}>Preview import</button>{summary && <><pre>{JSON.stringify(summary, null, 2)}</pre><button onClick={() => act(async () => setSummary(await outreach("import/commit", {
          csv
        }, "post")), "CSV import completed.")}>Import reviewed CSV</button></>}</details></>;
}
function ProspectDetail({
  base,
  data,
  act,
  busy,
  error,
  navigate
}) {
  const p = data.prospect;
  const displayName = prospectTitle(data);
  const [tab, setTab] = useState("profile");
  const [membership, changeMembership] = useFields({
    campaign_id: "",
    stage: "researching"
  });
  return <div className="outreach-prospect-detail"><Link className="outreach-link-button" to="/admin/outreach/prospects">Back to directory</Link><header className="bookstore-header"><h2>{displayName}</h2><div className="bookstore-actions"><button onClick={() => act(() => outreach(`prospects/${p.id}/suppress`, {}, "post"), "Do Not Contact saved; pending follow-ups cancelled.")}>Mark Do Not Contact</button><button onClick={() => {
          if (window.confirm("Archive this prospect and cancel pending follow-ups?")) act(() => outreach(`prospects/${p.id}`, {}, "delete"), "Prospect archived.");
        }}>Archive</button></div></header>{data.suppressed && <p className="bookstore-error">Outreach is suppressed for this prospect.</p>}
 {p.archived === 1 && <div className="outreach-card"><p>This prospect is archived. Restore it to the directory, or deliberately delete personal details while retaining suppression. Original legacy records and backups are managed separately.</p><button onClick={() => act(() => outreach(`prospects/${p.id}/restore`, {}, "post"), "Prospect restored. Opt-outs remain in force.")}>Restore prospect</button><button onClick={() => {
        if (window.confirm("Permanently delete this outreach record's personal details and message content? Suppression identifiers will remain. This cannot be undone through the interface.")) act(() => outreach(`prospects/${p.id}/delete-personal-data`, {
          confirm_delete: true
        }, "post"), "Personal details deleted; suppression retained.");
      }}>Delete personal details</button></div>}
 <div className="bookstore-contact-strip"><PublicLink href={p.website}>Open Website / Podcast</PublicLink>{data.contacts.map(c => c.method === "email" ? <span key={c.id}>{c.value}</span> : <PublicLink key={c.id} href={c.value}>Open {label(c.method)}</PublicLink>)}</div>
 <div className="bookstore-tabs">{["profile", "communications", "followups", "opportunities"].map(t => <button key={t} aria-pressed={tab === t} onClick={() => setTab(t)}>{label(t)}</button>)}</div>
 {tab === "profile" && <><ProspectForm base={base} busy={busy} initial={{
        ...p,
        display_name: displayName,
        contacts: data.contacts,
        books: data.books.map(b => b.book_title),
        category_ids: data.category_ids,
        profile: data.profile
      }} onSave={v => act(() => outreach(`prospects/${p.id}`, v, "put"))} /><h3>Campaign participation</h3>{data.memberships.map(m => <p key={m.campaign_id}>{m.title}: {label(m.stage)}{m.score != null && ` · Score ${m.score}/100 — ${m.score_explanation}`}</p>)}<form className="outreach-filter outreach-directory-filter" onSubmit={e => {
        e.preventDefault();
        act(() => outreach("participants", {
          ...membership,
          prospect_id: p.id
        }, "post"));
      }}><Field title="Campaign" name="campaign_id" required value={membership.campaign_id} onChange={changeMembership} options={base.campaigns.map(c => ({
          value: c.id,
          label: c.title
        }))} /><Field title="Stage" name="stage" value={membership.stage} onChange={changeMembership} options={base.stages} /><button>Save campaign stage</button></form>
 <OutreachScoreForm prospectId={p.id} campaigns={base.campaigns} disabled={data.sources.some(src => src.provider === "youtube")} act={act} />
 <h3>Discovery sources</h3>{data.sources.map(source => <article className="outreach-card" key={source.id}><PublicLink href={source.source_url}>Open {source.provider === "youtube" ? "YouTube channel" : "podcast"}</PublicLink><p className="bookstore-muted">Fetched {date(source.fetched_at)} · Expires {date(source.expires_at)}</p>{source.metadata?.name ? <><h4>{source.metadata.name}</h4><p>{source.metadata.description}</p><p>Public audience: {source.metadata.audience_size ?? "Unknown"}</p><div className="bookstore-actions"><PublicLink href={source.metadata.website}>Open website</PublicLink><PublicLink href={source.metadata.feed_url}>Open podcast feed</PublicLink></div><div className="outreach-recent-content">{source.metadata.recent?.map((r, n) => <div className="outreach-recent-item" key={n}><h4>{r.title}</h4>{source.provider === "youtube" && <OutreachVideo url={r.url} title={r.title} />}<div className="bookstore-actions"><PublicLink href={r.url}>{source.provider === "youtube" ? "Watch on YouTube" : "Open episode"}</PublicLink><span className="bookstore-muted">{date(r.published_at)}</span></div></div>)}</div></> : <p>Provider metadata is unavailable or expired.</p>}</article>)}{!!data.sources.length && <button onClick={() => act(() => outreach(`prospects/${p.id}/refresh`, {}, "post"), "Provider data refreshed.")}>Refresh provider data</button>}
 <h3>Potential duplicates</h3>{!data.duplicates.length && <p>No potential duplicates found.</p>}{data.duplicates.map(d => <div className="outreach-card" key={d.id}><ProspectLink id={d.id}>{d.name}</ProspectLink><p>{d.reason} · {label(d.decision)}</p>{["keep_separate", "ignore", "review_later"].map(decision => <button key={decision} onClick={() => act(() => outreach(`prospects/${p.id}/duplicates`, {
          other_id: d.id,
          decision
        }, "post"))}>{label(decision)}</button>)}<button onClick={() => {
          if (window.confirm(`Merge this record into ${d.name}? Communication history and opt-outs will be preserved.`)) act(async () => {
            const r = await outreach(`prospects/${p.id}/merge`, {
              target_id: d.id
            }, "post");
            navigate(`/admin/outreach/prospects/${r.id}`);
          }, "Prospects merged.");
        }}>Merge into this prospect</button></div>)}</>}
 {tab === "communications" && <><Composer base={base} data={data} act={act} /><MessageList messages={data.messages} deliveries={data.deliveries} act={act} /></>}
 {tab === "followups" && <Tasks base={base} data={{
      tasks: data.tasks
    }} act={act} prospect={p} />}
 {tab === "opportunities" && <><Workflow type="offers" base={base} act={act} busy={busy} error={error} prospect={p} /><Workflow type="outcomes" base={base} act={act} busy={busy} error={error} prospect={p} /><h3>Related events</h3>{data.events.map(e => <p key={e.id}>{e.title} — {date(e.starts_at)}</p>)}<Link to="/admin/outreach/events">Manage events and interviews</Link></>}
 </div>;
}
function Composer({
  base,
  data,
  act
}) {
  const p = data.prospect;
  const [templates, setTemplates] = useState([]),
    [template, setTemplate] = useState(""),
    [preview, setPreview] = useState(null),
    [requestId, setRequestId] = useState(null);
  const [v, change, set] = useFields({
    recipient: data.contacts.find(c => c.method === "email")?.value || "",
    campaign_id: "",
    subject: "",
    body_html: "",
    channel: "email",
    direction: "outgoing",
    book_title: "",
    book_link: ""
  });
  useEffect(() => {
    outreach("templates").then(r => setTemplates(r.templates)).catch(() => {});
  }, []);
  const edit = e => {
    change(e);
    setPreview(null);
    setRequestId(null);
  };
  const draft = {
    recipient: v.recipient,
    campaign_id: v.campaign_id,
    subject: v.subject,
    body_html: v.body_html
  };
  return <section><h3>Compose a message</h3><p>Email is sent through the existing website mail service after preview and your confirmation. External messages can be copied and recorded manually.</p><div className="bookstore-fields"><Field title="Recipient email" name="recipient" value={v.recipient} onChange={edit} /><Field title="Campaign" name="campaign_id" value={v.campaign_id} onChange={edit} options={data.memberships.map(c => ({
        value: c.campaign_id,
        label: c.title
      }))} /><Field title="Book" name="book_title" value={v.book_title} onChange={edit} options={base.books.map(b => b.title)} /><Field title="Verified book link (for templates)" name="book_link" value={v.book_link} onChange={edit} /><Field title="Template" value={template} onChange={e => setTemplate(e.target.value)} options={templates.map(t => ({
        value: t.template_name,
        label: label(t.template_name)
      }))} /><button onClick={() => act(async () => {
        const t = templates.find(r => r.template_name === template);
        if (!t) throw new Error("Choose a template.");
        const r = await outreach("templates/render", {
          subject: t.subject,
          body_html: t.body_html,
          values: {
            prospect_name: prospectTitle(data),
            organization_name: p.organization || prospectTitle(data),
            campaign_title: base.campaigns.find(c => String(c.id) === String(v.campaign_id))?.title,
            book_title: v.book_title,
            book_description: base.books.find(b => b.title === v.book_title)?.description,
            book_link: v.book_link,
            author_name: "Kenneth L. Powell",
            website_url: "https://kennethlpowell.com"
          }
        }, "post");
        set({
          ...v,
          ...r
        });
        setPreview(null);
      }, "Template loaded. Review and personalize the draft.")}>Load template</button></div>
 <Field title="Load a saved draft" value="" options={data.messages.filter(m => m.status === "draft").map(m => ({
      value: m.id,
      label: `${m.subject} — ${date(m.occurred_at)}`
    }))} onChange={e => {
      const m = data.messages.find(m => String(m.id) === e.target.value);
      if (m) {
        set({
          ...v,
          recipient: m.recipient || v.recipient,
          campaign_id: m.campaign_id || "",
          subject: m.subject,
          body_html: m.body_html
        });
        setPreview(null);
        setRequestId(null);
      }
    }} />
 <Field title="Subject" name="subject" value={v.subject} onChange={edit} /><Field title="Message (HTML supported)" name="body_html" type="textarea" value={v.body_html} onChange={edit} />
 <div className="bookstore-actions"><button onClick={() => act(() => outreach(`prospects/${p.id}/messages`, {
        ...draft,
        status: "draft",
        direction: "outgoing",
        channel: v.channel
      }, "post"), "Draft saved in communication history.")}>Save draft</button><button onClick={() => act(() => navigator.clipboard.writeText(new DOMParser().parseFromString(v.body_html, "text/html").body.textContent || ""), "Message copied.")}>Copy message</button><button disabled={data.suppressed} onClick={() => act(async () => {
        setPreview(await outreach(`prospects/${p.id}/email-preview`, draft, "post"));
        setRequestId(window.crypto.randomUUID());
      }, "Preview ready. Check the recipient and content before sending.")}>Preview email</button></div>
 {preview && <div className="outreach-card"><p>From: {preview.sender} · To: {preview.recipient}</p><strong>{preview.subject}</strong><iframe title="Final outreach email preview" sandbox="" className="bookstore-email-preview" srcDoc={preview.html} /><button onClick={() => {
        if (window.confirm(`Send this email to ${preview.recipient}?`)) act(async () => {
          const result = await outreach(`prospects/${p.id}/email`, {
            ...draft,
            preview_token: preview.preview_token,
            request_id: requestId,
            confirm_send: true
          }, "post");
          setPreview(null);
          return result;
        }, "Email attempt recorded. Review history for provider status.");
      }}>Confirm and send this email</button></div>}
 <h3>Record an external contact or response</h3><div className="bookstore-fields"><Field title="Channel" name="channel" value={v.channel} onChange={change} options={base.methods} /><Field title="Direction" name="direction" value={v.direction} onChange={change} options={["incoming", "outgoing", "internal"]} /></div><button onClick={() => {
      if (v.direction !== "outgoing" || window.confirm("Confirm that you actually sent this message outside the website?")) act(() => outreach(`prospects/${p.id}/messages`, {
        ...draft,
        channel: v.channel,
        direction: v.direction,
        status: v.direction === "outgoing" ? "manual" : "recorded",
        confirm_contact: true
      }, "post"), "Communication recorded.");
    }}>Record communication</button></section>;
}
function MessageList({
  messages,
  deliveries = [],
  act
}) {
  return <><h3>Communication history</h3>{!messages.length && <p>No communications recorded.</p>}{messages.map(m => <article className="outreach-card" key={m.id}><strong>{m.subject}</strong><p>{label(m.direction)} · {m.channel} · {m.status} · {date(m.occurred_at)}</p>{deliveries.filter(d => d.provider_message_id === m.provider_message_id).map((d, n) => <p key={n}>Provider delivery evidence: {d.status} — {date(d.event_at)}</p>)}<details><summary>Read message</summary><pre>{m.body_text}</pre></details>{["unknown", "sending"].includes(m.status) && <form onSubmit={e => {
        e.preventDefault();
        const form = new FormData(e.currentTarget);
        act(() => outreach(`messages/${m.id}/resolve`, {
          status: form.get("status"),
          evidence: form.get("evidence")
        }, "post"));
      }}><label>Verified provider outcome<select name="status"><option value="accepted">Accepted</option><option value="failed">Definitely failed</option></select></label><label>Provider evidence<textarea name="evidence" required /></label><button>Record verified resolution</button></form>}</article>)}</>;
}
function Communications({
  data,
  act,
  exportRows
}) {
  return <><h2>Communications</h2><p>Open a prospect to compose email, reuse a saved draft, or record an external conversation.</p><button onClick={() => exportRows("messages")}>Export communication history</button><MessageList messages={data.messages} act={act} /></>;
}
function Discovery({
  data,
  act,
  discoverySession,
  setDiscoverySession
}) {
  const {values:v,results}=discoverySession;
  const change=e=>{const {name,value}=e.target;setDiscoverySession(previous=>({...previous,values:{...previous.values,[name]:value}}));};
  const setResults=update=>setDiscoverySession(previous=>({...previous,results:typeof update==="function"?update(previous.results):update}));
  const replaceResults=(output,values=v)=>setDiscoverySession({values,results:output,expiresAt:Date.now()+discoveryLifetime});
  const run = page => act(async () => replaceResults(await outreach("discovery/search", {
    ...v,
    page_token: page || null
  }, "post")), "Discovery search complete. Review each result before saving.");
  return <><h2>Prospect Discovery</h2><p>Discover channels and podcasts, then review their public sites for contact instructions. Search results are never contacted automatically.</p><form className="outreach-filter outreach-discovery-search" onSubmit={e => {
      e.preventDefault();
      run();
    }}><Field title="Provider" name="provider" value={v.provider} onChange={change} options={["youtube", "podcastindex"]} /><Field title="Keywords" name="keywords" required value={v.keywords} onChange={change} />{v.provider === "youtube" && <><Field title="Minimum public subscribers" type="number" min="0" name="min_audience" value={v.min_audience} onChange={change} /><Field title="Maximum public subscribers" type="number" min="0" name="max_audience" value={v.max_audience} onChange={change} /></>}<button>Search</button></form>
 <form className="outreach-filter outreach-discovery-save" onSubmit={e => {
      e.preventDefault();
      act(() => outreach("saved-searches", v, "post"), "Search saved.");
    }}><Field title="Save this search as" required name="name" value={v.name} onChange={change} /><button>Save search</button></form>
 {!!data.searches.length && <details><summary>Saved searches</summary>{data.searches.map(s => <p key={s.id}>{s.name} ({s.provider}) · Last run: {date(s.last_run_at)} · {s.result_count ?? "—"} results <button onClick={() => act(async () => replaceResults(await outreach(`saved-searches/${s.id}/run`, {}, "post"),{...v,...s.parameters,provider:s.provider,name:s.name}), "Saved search completed.")}>Run again</button></p>)}</details>}
 {results && <><button onClick={()=>{if(window.confirm("Finish reviewing and clear these discovery results? Your saved prospects will remain in the directory."))setDiscoverySession(previous=>({...previous,results:null,expiresAt:null}));}}>Done reviewing</button><p>{results.note} {results.cached && "Using cached provider data."}</p>{results.results.length === 0 && <p>No matching results in this response.</p>}{results.results.map(r => <article className="outreach-card" key={`${r.provider}-${r.external_id}`}><h3>{r.name}</h3><p>{r.description}</p><div className="bookstore-contact-strip"><PublicLink href={r.source_url}>Open channel / podcast</PublicLink><PublicLink href={r.website}>Open website</PublicLink><PublicLink href={r.feed_url}>Open feed</PublicLink></div><p>Public audience: {r.audience_size ?? "Unknown"} · {r.provider === "youtube" ? "No API-derived suitability score" : "Review relevance before outreach"}</p><button onClick={() => act(async () => {
          const details = await outreach(`discovery/${r.provider}/${encodeURIComponent(r.external_id)}`);
          setResults(previous=>({...previous,results:previous.results.map(x=>x.provider===r.provider&&x.external_id===r.external_id?{...x,...(details.results[0]||{})}:x)}));
        }, "Recent content loaded.")}>Inspect recent content</button><button disabled={Boolean(r.saved_prospect_id)} onClick={() => act(async () => {
          const saved = await outreach("discovery/save", {
            provider: r.provider,
            external_id: r.external_id
          }, "post");
          setResults(previous=>({...previous,results:previous.results.map(x=>x.provider===r.provider&&x.external_id===r.external_id?{...x,saved_prospect_id:saved.id}:x)}));
        }, "Prospect saved for review. Your discovery results are still available.")}>{r.saved_prospect_id?"Saved for review":"Save for review"}</button>{r.saved_prospect_id&&<ProspectLink id={r.saved_prospect_id}>View prospect</ProspectLink>}{r.recent?.map((ep, n) => <p key={n}><PublicLink href={ep.url}>{ep.title}</PublicLink> · {date(ep.published_at)}</p>)}</article>)}{results.next_page_token && <button onClick={() => run(results.next_page_token)}>Next provider results</button>}</>}
 </>;
}
const activityOptions = [{value:"active",label:"Active"},{value:"inactive",label:"Inactive"},{value:"all",label:"All"}];
const matchesActivity = (record, activity) => activity === "all" || Boolean(Number(record.archived || 0)) === (activity === "inactive");
function Campaigns({
  base,
  data,
  act,
  busy,
  error
}) {
  const [editing, setEditing] = useState(null),
    [activity, setActivity] = useState("active"),
    [detail, setDetail] = useState(null);
  const visible = data.campaigns.filter(c => matchesActivity(c, activity));
  return <><h2>Campaigns</h2><button onClick={() => {
      setEditing({});
      setDetail(null);
    }}>Create campaign</button><div className="outreach-filter"><Field title="Campaign visibility" value={activity} options={activityOptions} onChange={e => {setActivity(e.target.value);setDetail(null);}} /></div><Table headers={["Campaign", "Status", "Prospects", "Actions"]} rows={visible.map(c => <tr key={c.id}><td>{c.title}<small>ID {c.id}{Number(c.archived) === 1 && " · Inactive"}</small></td><td>{c.status}</td><td>{c.prospect_count}</td><td><button onClick={() => act(async () => {
          const r = await outreach(`campaigns/${c.id}`);
          setDetail(r);
          setEditing(null);
        }, "Campaign loaded.")}>View</button><button onClick={() => act(async () => {
          const r = await outreach(`campaigns/${c.id}`);
          setEditing({
            ...r.campaign,
            books: r.books.map(b => b.book_title)
          });
          setDetail(null);
        }, "Campaign ready to edit.")}>Edit</button><button onClick={() => act(() => outreach(`campaigns/${c.id}/duplicate`, {}, "post"), "Campaign duplicated without copying prospects.")}>Duplicate</button><button onClick={() => act(async () => {
          await outreach(`campaigns/${c.id}/activity`, {active: Number(c.archived) === 1}, "put");
          setDetail(null);
        }, Number(c.archived) === 1 ? "Campaign reactivated." : "Campaign inactivated. Find it using the Inactive filter.")}>{Number(c.archived) === 1 ? "Reactivate" : "Inactivate"}</button></td></tr>)} />
 {editing && <OutreachModal title={editing.id ? "Edit campaign" : "Create campaign"} submitLabel="Save campaign" busy={busy} error={error} onClose={() => setEditing(null)}>{formId => <CampaignForm formId={formId} key={editing.id || "new"} initial={editing} base={base} onSave={async v => {
      const saved = await act(() => outreach(editing.id ? `campaigns/${editing.id}` : "campaigns", v, editing.id ? "put" : "post"));
      if (saved) setEditing(null);
    }} />}</OutreachModal>}
 {detail && <section><h3>{detail.campaign.title}</h3><p>{detail.campaign.objectives}</p><p>{detail.books.map(b => b.book_title).join(", ")}</p><Table headers={["Prospect", "Stage", "Response"]} rows={detail.participants.map(p => <tr key={p.prospect_id}><td><ProspectLink id={p.prospect_id}>{p.name}</ProspectLink></td><td>{label(p.stage)}</td><td>{p.response || "—"}</td></tr>)} /></section>}
 <Workflow type="kits" base={base} act={act} busy={busy} error={error} /><Workflow type="links" base={base} act={act} busy={busy} error={error} /></>;
}
function CampaignForm({
  base,
  formId,
  initial,
  onSave
}) {
  const [v, change, set] = useFields({
    ...initial,
    books: initial.books || []
  });
  return <form id={formId} onSubmit={e => {
    e.preventDefault();
    onSave(v);
  }}><div className="bookstore-fields"><Field title="Title" name="title" required value={v.title} onChange={change} /><Field title="Category" name="category_id" value={v.category_id} onChange={change} options={base.categories.map(c => ({
        value: c.id,
        label: c.name
      }))} /><Field title="Status" name="status" value={v.status || "draft"} onChange={change} options={["draft", "active", "paused", "completed", "template"]} /><Field title="Start date" name="starts_at" type="date" value={v.starts_at?.slice(0, 10)} onChange={change} /><Field title="End date" name="ends_at" type="date" value={v.ends_at?.slice(0, 10)} onChange={change} /></div><div className="outreach-text-fields">{["description", "objectives", "notes"].map(f => <Field key={f} title={label(f)} name={f} type="textarea" value={v[f]} onChange={change} />)}</div><BookSelect books={base.books} value={v.books} onChange={books => set({
      ...v,
      books
    })} />{!formId && <button>Save campaign</button>}</form>;
}
function Tasks({
  base,
  data,
  act,
  prospect,
  exportRows
}) {
  const [v, change, set] = useFields({
    prospect_id: prospect?.id || "",
    campaign_id: "",
    title: "Follow up",
    due_at: localDate(new Date(Date.now() + 7 * 86400000))
  });
  return <><h2>Follow-ups</h2><p>Follow-ups are tasks for you. They never send automatically. Declined campaigns and global opt-outs block further follow-ups.</p><button onClick={() => {
      const sequence = data.tasks.filter(t => String(t.prospect_id) === String(v.prospect_id) && String(t.campaign_id || "") === String(v.campaign_id || "") && t.status !== "cancelled").length;
      const days = sequence ? base.followup_defaults?.second_followup_days || 14 : base.followup_defaults?.followup_days || 7;
      set({
        ...v,
        due_at: localDate(new Date(Date.now() + days * 86400000))
      });
    }}>Use configured follow-up interval from today</button><form className="outreach-filter" onSubmit={e => {
      e.preventDefault();
      act(() => outreach("tasks", {
        ...v,
        due_at: utc(v.due_at)
      }, "post"));
    }}>{!prospect && <Field title="Prospect" name="prospect_id" type="prospect" required value={v.prospect_id} onChange={change} />}<Field title="Campaign" name="campaign_id" value={v.campaign_id} onChange={change} options={base.campaigns.map(c => ({
        value: c.id,
        label: c.title
      }))} /><Field title="Task" name="title" required value={v.title} onChange={change} /><Field title="Due (your local time)" name="due_at" type="datetime-local" required value={v.due_at} onChange={change} /><button>Schedule follow-up</button></form>
 {exportRows && <button onClick={() => exportRows("tasks")}>Export tasks</button>}<Table headers={["Prospect", "Task", "Due", "Status", "Actions"]} rows={data.tasks.map(t => <tr key={t.id}><td><ProspectLink id={t.prospect_id}>{t.name || prospect?.name}</ProspectLink></td><td>{t.title} · #{t.sequence_number}</td><td>{date(t.due_at)}</td><td>{t.status}</td><td>{t.status === "pending" && <><button onClick={() => act(() => outreach(`tasks/${t.id}`, {
            status: "completed"
          }, "put"))}>Complete</button><button onClick={() => {
            const value = window.prompt("Postpone to local date/time (YYYY-MM-DDTHH:MM)", localDate(t.due_at));
            if (value) act(() => outreach(`tasks/${t.id}`, {
              status: "pending",
              due_at: utc(value)
            }, "put"));
          }}>Postpone</button><button onClick={() => act(() => outreach(`tasks/${t.id}`, {
            status: "cancelled"
          }, "put"))}>Cancel</button></>}</td></tr>)} /></>;
}
function Applications({
  data,
  act
}) {
  return <><h2>Collaboration Applications</h2><p>Approval creates a research prospect. Application consent is separate from newsletter subscription. No acceptance email is sent automatically.</p>{!data.applications.length && <p>No applications yet.</p>}{data.applications.map(a => <article className="outreach-card" key={a.id}><h3>{a.name} — {a.category}</h3><p>{a.email} · {a.organization} · {a.status}</p><PublicLink href={a.website}>Open public website</PublicLink><pre>{a.message}</pre><p>Contact consent: {date(a.consent_at)}</p>{["approved", "declined", "information_requested"].map(status => <button key={status} onClick={() => act(() => outreach(`applications/${a.id}/review`, {
        status
      }, "post"), "Application updated. Compose any response from the prospect profile.")}>{label(status)}</button>)}{a.prospect_id && <ProspectLink id={a.prospect_id}>Open prospect</ProspectLink>}</article>)}</>;
}
function Events({
  base,
  data,
  act,
  busy,
  error,
  exportRows
}) {
  const [editing, setEditing] = useState(null),
    [mode, setMode] = useState("upcoming"),
    [day, setDay] = useState(new Date().toLocaleDateString("en-CA"));
  const start = new Date(`${day}T00:00:00`),
    end = new Date(start);
  if (mode === "day") end.setDate(end.getDate() + 1);else if (mode === "week") end.setDate(end.getDate() + 7);else if (mode === "month") {
    start.setDate(1);
    end.setMonth(end.getMonth() + 1, 1);
  }
  const events = data.events.filter(e => mode === "all" || mode === "upcoming" ? mode === "all" || new Date(e.starts_at) >= new Date() : new Date(e.starts_at) >= start && new Date(e.starts_at) < end);
  return <><h2>Events &amp; Interviews</h2><div className="outreach-filter"><button onClick={() => setEditing({})}>Create event</button><Field title="Calendar view" value={mode} options={["upcoming", "day", "week", "month", "all"]} onChange={e => setMode(e.target.value)} /><Field title="Date" type="date" value={day} onChange={e => setDay(e.target.value)} /><button onClick={() => act(async () => {
        const response = await fetch(`${BASE_URL}/api/outreach/calendar.ics`, {
          headers: {
            Authorization: `Dragonian ${Api.token}`
          }
        });
        if (!response.ok) throw new Error("Calendar export failed.");
        download(await response.text(), "outreach-calendar.ics", "text/calendar");
      }, "Calendar downloaded.")}>Export calendar</button><button onClick={() => exportRows("events")}>Export events CSV</button></div>
 {editing && <OutreachModal title={editing.id ? "Edit event" : "Create event"} submitLabel="Save event" busy={busy} error={error} onClose={() => setEditing(null)}>{formId => <EventForm formId={formId} key={editing.id || "new"} base={base} initial={editing} onSave={async v => {
      const saved = await act(() => outreach(editing.id ? `events/${editing.id}` : "events", v, editing.id ? "put" : "post"));
      if (saved) setEditing(null);
    }} />}</OutreachModal>}
 <Table headers={["Event", "Type", "Time", "Status", "Actions"]} rows={events.map(e => <tr key={e.id}><td>{e.title}<small>{e.name}</small></td><td>{label(e.event_type)}</td><td>{date(e.starts_at)}<small>{e.time_zone}</small></td><td>{e.status}</td><td><button onClick={() => setEditing(e)}>Edit / cancel</button><PublicLink href={e.publication_url}>Published interview</PublicLink></td></tr>)} /><Workflow type="offers" base={base} act={act} busy={busy} error={error} /><Workflow type="outcomes" base={base} act={act} busy={busy} error={error} /></>;
}
function EventForm({
  base,
  formId,
  initial,
  onSave
}) {
  const [v, change] = useFields({
    ...initial,
    starts_at: localDate(initial.starts_at),
    ends_at: localDate(initial.ends_at),
    time_zone: initial.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    event_type: initial.event_type || "interview",
    status: initial.status || "proposed"
  });
  return <form id={formId} onSubmit={e => {
    e.preventDefault();
    onSave({
      ...v,
      starts_at: utc(v.starts_at),
      ends_at: utc(v.ends_at)
    });
  }}><div className="bookstore-fields">{[["title", "Event title", "text", true], ["prospect_id", "Prospect", "prospect"], ["starts_at", "Starts (your local time)", "datetime-local", true], ["ends_at", "Ends (your local time)", "datetime-local"], ["time_zone", "Event IANA time zone", "text", true], ["location", "Location / meeting link", "text"], ["publication_url", "Published episode/video URL", "url"]].map(([f, title, type, required]) => <Field key={f} name={f} title={title} type={type} required={required} value={v[f]} onChange={change} />)}<Field title="Campaign" name="campaign_id" value={v.campaign_id} onChange={change} options={base.campaigns.map(c => ({
        value: c.id,
        label: c.title
      }))} /><Field title="Type" name="event_type" value={v.event_type} onChange={change} options={["interview", "podcast", "book_club", "library", "bookstore", "deadline", "other"]} /><Field title="Status" name="status" value={v.status} onChange={change} options={["proposed", "scheduled", "completed", "cancelled"]} /></div><Field title="Guest biography, topics, questions, recording arrangements and notes" name="notes" type="textarea" value={v.notes} onChange={change} />{!formId && <button>Save event</button>}</form>;
}
const workflowFields = {
  offers: [["prospect_id", "Prospect", "prospect", true], ["campaign_id", "Campaign", "campaign"], ["book_title", "Book", "book", true], ["format", "Format", ["ebook", "pdf", "paperback", "hardcover", "audiobook", "other"]], ["status", "Status", ["requested", "offered", "delivered", "reviewed", "declined"]], ["offered_at", "Offered", "datetime-local"], ["delivered_at", "Delivered", "datetime-local"], ["review_url", "Voluntary review URL", "url"], ["completed_at", "Completed", "datetime-local"], ["notes", "Notes / existing delivery reference", "textarea"]],
  outcomes: [["prospect_id", "Prospect", "prospect", true], ["campaign_id", "Campaign", "campaign"], ["outcome_type", "Outcome", ["review", "book_club_selection", "podcast_appearance", "interview", "library_opportunity", "bookstore_opportunity", "collaboration_completed", "declined", "other"], true], ["occurred_at", "Date", "datetime-local", true], ["url", "Public result URL", "url"], ["notes", "Notes", "textarea"]],
  kits: [["book_title", "Book", "book", true], ["title", "Discussion kit title", "text", true], ["content", "Overview, spoiler-free introduction, questions, author biography and Q&A information", "textarea", true], ["spoiler_content", "Optional spoiler discussion", "textarea"]],
  links: [["campaign_id", "Campaign", "campaign", true], ["book_title", "Book", "book"], ["label", "Link label", "text", true], ["url", "Destination URL", "url", true], ["medium", "Medium", ["email", "social", "referral"]]]
};
function Workflow({
  type,
  base,
  act,
  busy,
  error,
  prospect
}) {
  const [rows, setRows] = useState([]),
    [activity, setActivity] = useState("active"),
    [editing, setEditing] = useState(null);
  const requestVersion = useRef(0);
  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    const r = await outreach(type, type === "kits" ? {activity} : undefined);
    if (version === requestVersion.current) setRows(r[type]);
  }, [type, activity]);
  useEffect(() => {
    setRows([]);
    load().catch(() => {});
    return () => {requestVersion.current++;};
  }, [load]);
  const names = {
    offers: "Review-copy requests & offers",
    outcomes: "Outcomes",
    kits: "Book club discussion kits",
    links: "Campaign attribution links"
  };
  const formNames = {offers: "review-copy offer", outcomes: "outcome", kits: "discussion kit", links: "campaign link"};
  return <section className="outreach-workflow"><h3>{names[type]}</h3>{type === "offers" && <p>Track delivery through the existing store/coupon tools. Reviews are voluntary and never conditional on a favorable rating.</p>}{type === "links" && <p>UTM links preserve destination behavior. External retailer clicks do not establish sales.</p>}<button onClick={() => setEditing({
      prospect_id: prospect?.id || ""
    })}>Add {label(type)}</button>{type === "kits" && <div className="outreach-filter"><Field title="Kit visibility" value={activity} options={activityOptions} onChange={e => setActivity(e.target.value)} /></div>}{editing && <OutreachModal title={`${editing.id ? "Edit" : "Create"} ${formNames[type]}`} busy={busy} error={error} onClose={() => setEditing(null)}>{formId => <WorkflowForm formId={formId} key={editing.id || "new"} type={type} base={base} initial={editing} onSave={async v => {
      let saved = false;
      await act(async () => {
      await outreach(editing.id ? `${type}/${editing.id}` : type, v, editing.id ? "put" : "post");
      saved = true;
      await load();
      });
      if (saved) setEditing(null);
    }} />}</OutreachModal>}<ul>{rows.filter(r => (!prospect || r.prospect_id === prospect.id) && (type !== "kits" || matchesActivity(r, activity))).map(r => <li key={r.id}><span>{r.title || r.label || r.book_title || label(r.outcome_type)} {r.status && `— ${r.status}`} {type === "kits" && Number(r.archived) === 1 && "— Inactive"} </span>{r.url && <PublicLink href={r.url}>Open link</PublicLink>}{type !== "links" && <button onClick={() => setEditing(r)}>Edit</button>}{type === "kits" && <><button onClick={() => act(async () => {
          download([r.title, r.book_title, r.content, "SPOILER DISCUSSION", r.spoiler_content || ""].join("\n\n"), "discussion-kit.txt");
        }, "Discussion kit downloaded for review.")}>Download kit</button><button onClick={() => act(async () => {
          await outreach(`kits/${r.id}/activity`, {active: Number(r.archived) === 1}, "put");
          await load();
        }, Number(r.archived) === 1 ? "Kit reactivated." : "Kit inactivated. Find it using the Inactive filter.")}>{Number(r.archived) === 1 ? "Reactivate" : "Inactivate"}</button></>}</li>)}</ul></section>;
}
function WorkflowForm({
  type,
  formId,
  base,
  initial,
  onSave
}) {
  const normalized = {
    ...initial
  };
  workflowFields[type].forEach(([f,, t]) => {
    if (t === "datetime-local") normalized[f] = localDate(initial[f]);
  });
  const [v, change] = useFields(normalized);
  return <form id={formId} onSubmit={e => {
    e.preventDefault();
    const body = {
      ...v
    };
    workflowFields[type].forEach(([f,, t]) => {
      if (t === "datetime-local") body[f] = utc(body[f]);
    });
    onSave(body);
  }}><div className="bookstore-fields">{workflowFields[type].map(([name, title, type, required]) => <Field key={name} name={name} title={title} required={required} value={v[name]} onChange={change} type={typeof type === "string" ? type : "text"} options={Array.isArray(type) ? type : type === "book" ? base.books.map(b => b.title) : type === "campaign" ? base.campaigns.map(c => ({
        value: c.id,
        label: c.title
      })) : undefined} />)}</div>{!formId && <button>Save</button>}</form>;
}
function Analytics({
  base,
  data,
  act,
  exportRows
}) {
  const [result, setResult] = useState(data),
    [v, change] = useFields({
      campaign_id: "",
      category_id: "",
      platform: "",
      book_title: "",
      from: "",
      to: ""
    });
  useEffect(() => setResult(data), [data]);
  return <><h2>Campaign Analytics</h2><form className="outreach-filter" onSubmit={e => {
      e.preventDefault();
      act(async () => setResult(await outreach("analytics", {
        ...v,
        from: utc(v.from),
        to: utc(v.to)
      })), "Report updated.");
    }}><Field title="Campaign" name="campaign_id" value={v.campaign_id} onChange={change} options={base.campaigns.map(c => ({
        value: c.id,
        label: c.title
      }))} /><Field title="Category" name="category_id" value={v.category_id} onChange={change} options={base.categories.map(c => ({
        value: c.id,
        label: c.name
      }))} /><Field title="Book" name="book_title" value={v.book_title} onChange={change} options={base.books.map(b => b.title)} /><Field title="Platform" name="platform" value={v.platform} onChange={change} /><Field title="From (local time)" name="from" type="datetime-local" value={v.from} onChange={change} /><Field title="To (local time)" name="to" type="datetime-local" value={v.to} onChange={change} /><button>Filter report</button></form><div className="bookstore-summary">{[["Total prospects", result.total_prospects], ["Discovered this month", result.discovered_this_month], ["Contacted", result.contacted], ["Responses", result.responded], ["Follow-ups due", result.followups_due], ["Average response (hours)", result.average_response_hours == null ? "Unknown" : Math.round(result.average_response_hours)]].map(([title, n]) => <div key={title}><span>{title}<strong>{n ?? 0}</strong></span></div>)}</div><p>Response rate: {result.response_rate.percent == null ? "Not measurable" : `${result.response_rate.percent}%`} ({result.response_rate.numerator} responding prospects / {result.response_rate.denominator} contacted prospects).</p><p>{result.definitions}</p>{[["Campaign stages", result.stages, "stage"], ["Platforms", result.by_platform, "platform"], ["Categories", result.by_category, "name"], ["Outcomes", result.outcomes, "outcome_type"], ["Review copies", result.offers, "status"]].map(([title, rows, key]) => <section key={title}><h3>{title}</h3><ul>{rows.map(r => <li key={r[key]}>{label(r[key])}: {r.total}</li>)}</ul>{!rows.length && <p>No recorded activity yet.</p>}</section>)}<h3>Activity trends</h3><Table headers={["Month", "Direction", "Messages"]} rows={result.trends.map((r, n) => <tr key={n}><td>{r.month}</td><td>{r.direction}</td><td>{r.total}</td></tr>)} /><h3>Sales attribution</h3><p>{result.attribution.note}</p><dl>{[["Measured website sessions", result.attribution.measured_sessions], ["Book views", result.attribution.book_views], ["Newsletter signup events", result.attribution.signups], ["External retailer referrals", result.attribution.external_referrals], ["Attributed orders", result.attribution.orders], ["Attributed sales", result.attribution.sales]].map(([title, value]) => <div key={title}><dt>{title}</dt><dd>{value ?? "Unknown"}</dd></div>)}</dl><h3>Event outcomes</h3><ul>{result.events.map((r, n) => <li key={n}>{label(r.event_type)} — {r.status}: {r.total}</li>)}</ul><button onClick={() => download(reportCSV(result), "outreach-report.csv", "text/csv;charset=utf-8")}>Export this report CSV</button><button onClick={() => exportRows("outcomes")}>Export outcome records</button><button onClick={() => act(async () => download(JSON.stringify(result, null, 2), "outreach-report.json", "application/json"), "Report downloaded.")}>Download this report</button></>;
}
function Templates({
  data,
  act,
  busy,
  error
}) {
  const [selected, setSelected] = useState(null);
  return <><h2>Outreach Templates</h2><p>Supported variables: {data.variables.map(v => `{{${v}}}`).join(", ")}. Template rendering and final previews are available in each prospect's composer.</p><button onClick={() => setSelected({
      template_name: "outreach_"
    })}>New template</button><ul>{data.templates.map(t => <li key={t.template_name}><button onClick={() => setSelected(t)}>{label(t.template_name)}</button> — {t.subject}</li>)}</ul>{selected && <OutreachModal title={selected.template_name === "outreach_" ? "Create template" : "Edit template"} busy={busy} error={error} submitLabel="Save editable template" onClose={() => setSelected(null)}>{formId => <TemplateForm formId={formId} key={selected.template_name} initial={selected} onSave={async v => {const saved = await act(() => outreach("templates", v, "post"));if (saved) setSelected(null);}} />}</OutreachModal>}</>;
}
function TemplateForm({
  initial,
  formId,
  onSave
}) {
  const [v, change] = useFields(initial);
  return <form id={formId} onSubmit={e => {
    e.preventDefault();
    onSave(v);
  }}><Field title="Template name (outreach_ prefix)" name="template_name" required value={v.template_name} onChange={change} /><Field title="Subject" name="subject" required value={v.subject} onChange={change} /><Field title="Template HTML" name="body_html" type="textarea" required value={v.body_html} onChange={change} />{!formId && <button>Save editable template</button>}</form>;
}
function Settings({
  data,
  act
}) {
  const [v, change, set] = useFields(data.settings),
    [category, setCategory] = useState("");
  return <><h2>Settings &amp; Integrations</h2><p>Website mail sender: {data.email.sender || "Requires server configuration"}. {data.email.configured ? "Existing mail configuration is present." : "Mail configuration is missing on this server."}</p><p>{data.note}</p><form onSubmit={e => {
      e.preventDefault();
      act(() => outreach("settings", {
        ...v,
        weights: v.weights.map(Number)
      }, "put"));
    }}><div className="bookstore-fields"><Field title="Business mailing address (included in outreach email)" name="business_address" required type="textarea" value={v.business_address} onChange={change} /><Field title="Reply-to email (blank uses configured sender)" name="reply_to" type="email" value={v.reply_to} onChange={change} />{["youtube_search_cap", "youtube_detail_cap", "podcastindex_cap", "followup_days", "second_followup_days", "max_followups"].map(f => <Field key={f} title={label(f)} name={f} type="number" min="1" value={v[f]} onChange={change} />)}</div>{["youtube", "podcastindex"].map(p => <article className="outreach-card" key={p}><h3>{p}</h3><p>Credentials: {data.credentials[p] ? "Present" : "Awaiting server configuration"}</p><label><input type="checkbox" name={`${p}_enabled`} checked={Boolean(v[`${p}_enabled`])} onChange={change} />Enable this provider</label><p>Requests recorded today: {data.usage.filter(r => r.provider === p).map(r => `${r.bucket}: ${r.requests}`).join(", ") || "0"}</p>{data.health.filter(r => r.provider === p).map(h => <p key={h.provider}>Last success: {date(h.last_success_at)} · {h.last_error || "No recorded error"}</p>)}{p === "youtube" ? <p>Configure YOUTUBE_API_KEY in the backend environment. <PublicLink href="https://console.cloud.google.com/apis/library/youtube.googleapis.com">Enable YouTube Data API</PublicLink></p> : <p>Configure PODCAST_INDEX_API_KEY and PODCAST_INDEX_API_SECRET in the backend environment. <PublicLink href="https://api.podcastindex.org/">Podcast Index registration</PublicLink></p>}</article>)}<h3>Permitted-source scoring weights</h3><div className="bookstore-fields">{["Genre", "Recent activity", "Audience", "Collaboration"].map((title, n) => <Field key={title} title={title} type="number" min="0" value={v.weights[n]} onChange={e => set({
          ...v,
          weights: v.weights.map((w, k) => k === n ? Number(e.target.value) : w)
        })} />)}</div><p>Weights normalize internally. YouTube API data is excluded from automatic scoring.</p><label><input type="checkbox" name="notifications" checked={Boolean(v.notifications)} onChange={change} />Show outreach notifications</label><div className="bookstore-actions"><button>Save settings</button></div></form><h3>Additional categories</h3><form className="outreach-filter" onSubmit={e => {
      e.preventDefault();
      act(() => outreach("categories", {
        name: category
      }, "post"));
    }}><Field title="Category name" required value={category} onChange={e => setCategory(e.target.value)} /><button>Add category</button></form><h3>Existing bookstore/library records</h3><p>Import a linked snapshot into Outreach Manager. Existing records, prices, and history remain available in their original screens. Repeat imports skip previously linked records.</p><button onClick={() => {
      if (window.confirm("Import existing bookstore/library records and communication history into Outreach Manager?")) act(() => outreach("legacy-import", {
        confirm: true
      }, "post"), "Legacy outreach import completed; original records were preserved.");
    }}>Import existing outreach records</button></>;
}








